import { z } from 'zod';
/**
 * Schema de un Flow conversacional (ADR-0011 §flow-engine).
 *
 * Un Flow es un grafo dirigido de Steps. Cada Step tiene un `id` único dentro
 * del flow y un `type` que determina su ejecutor. Los Steps con `next` apuntan
 * al siguiente Step (o `null` para terminar). Los condition Steps tienen `branches`.
 */
const StepIdSchema = z.string().regex(/^[a-z][a-z0-9-]{0,63}$/, 'step.id must be kebab-case');
const BaseStepFields = {
    id: StepIdSchema,
    description: z.string().optional(),
};
// ── Step type: text ─────────────────────────────────────────────────────────
export const TextStepSchema = z.object({
    ...BaseStepFields,
    type: z.literal('text'),
    text: z.string().min(1).max(4096),
    /** Variable interpolation: ${context.foo} */
    next: StepIdSchema.nullable(),
});
// ── Step type: condition ────────────────────────────────────────────────────
const ConditionBranchSchema = z.object({
    when: z.string().min(1), // expression sobre context: ej. "context.intent == 'sales'"
    goto: StepIdSchema,
});
export const ConditionStepSchema = z.object({
    ...BaseStepFields,
    type: z.literal('condition'),
    branches: z.array(ConditionBranchSchema).min(1).max(20),
    default: StepIdSchema.nullable(),
    /**
     * Paridad legacy `ifMaxAttempts`: cuántas veces puede entrar al mismo step
     * antes de saltar a `maxAttemptsGoto`. Útil para validaciones interactivas
     * (ej. "responde sí/no" con 3 reintentos máx).
     */
    maxAttempts: z.number().int().min(1).max(10).optional(),
    maxAttemptsGoto: StepIdSchema.nullable().optional(),
});
// ── Step type: set-context ──────────────────────────────────────────────────
export const SetContextStepSchema = z.object({
    ...BaseStepFields,
    type: z.literal('set-context'),
    assign: z.record(z.string().regex(/^[a-zA-Z][a-zA-Z0-9_]{0,63}$/), z.union([z.string(), z.number(), z.boolean(), z.null()])),
    next: StepIdSchema.nullable(),
});
// ── Step type: handoff (publica HandoffRequested) ───────────────────────────
export const HandoffStepSchema = z.object({
    ...BaseStepFields,
    type: z.literal('handoff'),
    target: z.enum(['five9-digital', 'amazon-connect']),
    reason: z.string().optional(),
    /** Mensaje opcional al usuario antes de hacer el handoff (paridad legacy). */
    welcomeMessage: z.string().max(1024).optional(),
    /** Identificador externo (campaña Five9, queue Amazon Connect). */
    campaignName: z.string().max(128).optional(),
    next: StepIdSchema.nullable(), // post-handoff cleanup step
});
// ── Step type: encuesta (NPS — V1.1 stub) ───────────────────────────────────
export const EncuestaStepSchema = z.object({
    ...BaseStepFields,
    type: z.literal('encuesta'),
    surveyId: z.string().min(1),
    next: StepIdSchema.nullable(),
});
// ── Step type: http (call external API) ─────────────────────────────────────
export const HttpStepSchema = z.object({
    ...BaseStepFields,
    type: z.literal('http'),
    url: z.string().url(),
    method: z.enum(['GET', 'POST', 'PUT', 'DELETE']).default('GET'),
    /** Headers HTTP. Soporta interpolación ${context.X}. */
    headers: z.record(z.string(), z.string()).optional(),
    /** Body raw (string). Para JSON, serializar antes. Soporta interpolación. */
    body: z.string().optional(),
    /**
     * Autenticación básica. El runtime arma la cabecera: si el operador tuviera
     * que poner el base64 a mano, terminaría con credenciales escritas en el JSON
     * del flow. Los dos campos admiten `${context.x}` para sacarlas de una
     * variable del tenant.
     */
    auth: z
        .object({
        type: z.literal('basic'),
        username: z.string().min(1).max(256),
        password: z.string().min(1).max(256),
    })
        .optional(),
    timeoutMs: z.number().int().min(100).max(10000).default(5000),
    /** Si presente, guarda la respuesta (parseada JSON si content-type lo sugiere) en context[assignTo]. */
    assignTo: z.string().regex(/^[a-zA-Z][a-zA-Z0-9_]{0,63}$/).optional(),
    /** Si presente, ramifica por HTTP status code (2xx → next; otros → onError). */
    onError: StepIdSchema.nullable().optional(),
    next: StepIdSchema.nullable(),
});
// ── Step type: llm (OpenAI completion) ──────────────────────────────────────
const LlmButtonSchema = z.object({
    id: z.string().regex(/^[a-zA-Z0-9_-]{1,32}$/),
    label: z.string().min(1).max(80),
    goto: StepIdSchema.nullable(),
});
export const LlmStepSchema = z.object({
    ...BaseStepFields,
    type: z.literal('llm'),
    promptTemplate: z.string().min(1),
    model: z.string().default('gpt-4o-mini'),
    /** Si true, la respuesta del LLM se envía al usuario como mensaje (paridad legacy ai). */
    visibleToUser: z.boolean().optional(),
    /** Si presente, persiste la respuesta del LLM en context[assignTo]. */
    assignTo: z.string().regex(/^[a-zA-Z][a-zA-Z0-9_]{0,63}$/).optional(),
    /** Hasta 3 botones presentados al usuario tras la respuesta (paridad aiButtons). */
    buttons: z.array(LlmButtonSchema).max(3).optional(),
    /** Fallback si LLM falla (timeout, API error, sin output). */
    fallbackGoto: StepIdSchema.nullable().optional(),
    next: StepIdSchema.nullable(),
});
// ── Step type: document (WhatsApp media — paridad legacy `document`) ────────
// Envía un archivo (PDF/imagen/audio) al usuario. Webchat lo renderiza como
// link; WhatsApp lo envía como media message.
export const DocumentStepSchema = z.object({
    ...BaseStepFields,
    type: z.literal('document'),
    url: z.string().url(),
    filename: z.string().min(1).max(256).optional(),
    caption: z.string().max(1024).optional(),
    next: StepIdSchema.nullable(),
});
// ── Step type: send-template (paridad legacy `send_whatsapp_template`) ─────
// Envía una plantilla aprobada por Meta desde dentro del flujo.
//
// No es lo mismo que un step `text`: pasadas 24 horas desde el último mensaje
// del usuario, WhatsApp sólo acepta plantillas. Sin este step, un flujo que
// espera una respuesta que tarda —una aprobación, un pago— se queda mudo y no
// hay forma de retomar la conversación.
export const SendTemplateStepSchema = z.object({
    ...BaseStepFields,
    type: z.literal('send-template'),
    templateName: z.string().min(1).max(512),
    /** Código de idioma de la plantilla en Meta, p. ej. `es_CL`. */
    language: z.string().min(2).max(16),
    /**
     * Parámetros del cuerpo, en orden. Admiten `${context.x}` como el resto de
     * los steps: el valor casi siempre sale de lo que el flujo ya capturó.
     */
    params: z.array(z.string().max(1024)).max(20).optional(),
    /**
     * Parámetros con nombre, para plantillas que usan variables nombradas
     * (#183). Excluyente con `params`: Meta acepta una forma o la otra.
     */
    namedParams: z.record(z.string().max(64), z.string().max(1024)).optional(),
    next: StepIdSchema.nullable(),
});
// ── Step type: update-contact (paridad legacy `zengine_update_contact`) ────
// Guarda en la ficha del contacto lo que el flujo ya capturó.
//
// Sin esto, los datos que el usuario entrega dentro de una conversación —su
// nombre, su correo, si acepta notificaciones— viven sólo en el contexto de esa
// conversación y se pierden con su TTL. La próxima vez hay que volver a
// preguntar todo.
export const UpdateContactStepSchema = z.object({
    ...BaseStepFields,
    type: z.literal('update-contact'),
    /**
     * Campos a escribir. Los valores admiten `${context.x}` como el resto de los
     * steps. Sólo estos: el teléfono identifica al contacto y el estado es
     * decisión del operador, no del flujo.
     */
    fields: z.object({
        name: z.string().max(512).optional(),
        email: z.string().max(512).optional(),
        preferredChannel: z.string().max(64).optional(),
        notificationConsent: z.string().max(64).optional(),
        tags: z.array(z.string().max(64)).max(20).optional(),
    }),
    next: StepIdSchema.nullable(),
});
// ── Step type: flow-transfer (paridad legacy `flow_transfer`) ───────────────
// Cambia de flow dentro de la misma conversación. El runtime emite el evento
// FlowTransferRequested para que inbound-router cargue el target y continúe.
export const FlowTransferStepSchema = z.object({
    ...BaseStepFields,
    type: z.literal('flow-transfer'),
    targetFlowId: z.string().min(1).max(64),
    /** Step inicial dentro del target flow (opcional; si null usa el startStepId del target). */
    targetStartStepId: StepIdSchema.nullable().optional(),
    /** Next dentro del flow ACTUAL para limpieza si el transfer falla. */
    next: StepIdSchema.nullable(),
});
// ── Step type: interactive (options/buttons — paridad con legacy) ───────────
// Render diferenciado por canal: webchat usa lista de opciones inline,
// WhatsApp usa list message (presentation='list', hasta 10) o quick reply
// buttons (presentation='buttons', hasta 3).
const InteractiveChoiceSchema = z.object({
    id: z.string().regex(/^[a-zA-Z0-9_-]{1,32}$/),
    label: z.string().min(1).max(80),
    goto: StepIdSchema.nullable(),
});
/**
 * #135: optionsFromVar — paridad legacy. Permite poblar choices dinámicamente
 * desde una variable de contexto (array obtenido via http step previo),
 * usando JSONPath simple para extraer label/value de cada item.
 * Si `choicesFromContext` está presente, `choices` se ignora en runtime
 * (queda como fallback static si la resolución falla).
 */
const ChoicesFromContextSchema = z.object({
    /** Nombre de la variable de contexto (debe ser un array JSON). */
    arrayVar: z.string().regex(/^[a-zA-Z_][a-zA-Z0-9_]{0,63}$/),
    /** Path simple dot-notation para extraer label (ej. "name" o "user.name"). */
    labelPath: z.string().min(1).max(120),
    /** Path para extraer value (id de la choice). */
    valuePath: z.string().min(1).max(120),
    /** Opcional: prefix para sanitizar el id si valor contiene caracteres no válidos. */
    idPrefix: z.string().regex(/^[a-zA-Z0-9_-]{0,16}$/).optional(),
    /** Goto al elegir cualquiera de las choices dinámicas. */
    goto: StepIdSchema.nullable(),
});
export const InteractiveStepSchema = z.object({
    ...BaseStepFields,
    type: z.literal('interactive'),
    prompt: z.string().min(1).max(1024),
    presentation: z.enum(['list', 'buttons']).default('list'),
    choices: z.array(InteractiveChoiceSchema).min(1).max(10),
    choicesFromContext: ChoicesFromContextSchema.optional(),
    assignTo: z.string().regex(/^[a-zA-Z][a-zA-Z0-9_]{0,63}$/).optional(),
    fallback: StepIdSchema.nullable(),
});
// ── Step type: js (#129 — script in-line para lógica custom) ────────────────
/**
 * Paridad con el nodo `jsCode` del builder legacy. Ejecuta un script JS
 * en sandbox node:vm con timeout. El script recibe `vars` (objeto del
 * contexto del flow) y debe retornar (o asignar a `result`) un objeto:
 *
 *   - `{ assign: { foo: 'bar' } }` → merge en context.
 *   - `{ next: 'step-id' }` → salta a step específico (override del next).
 *
 * Limitaciones de seguridad (no negociables):
 *   - Sin `require`, `import`, `fs`, `net`, `process`, `globalThis`.
 *   - Sin `setTimeout`/`setInterval` (no async).
 *   - timeoutMs hard cap 1000ms (default 200).
 */
export const JsStepSchema = z.object({
    ...BaseStepFields,
    type: z.literal('js'),
    /** Código JS plano. Se ejecuta sin transpilación. */
    script: z.string().min(1).max(8192),
    timeoutMs: z.number().int().min(10).max(1000).default(200),
    /** Step destino por defecto si el script no retorna `{ next }`. */
    next: StepIdSchema.nullable(),
    /** Step destino si el script lanza/timeout. Si no, marca el flow failed. */
    onError: StepIdSchema.nullable().optional(),
});
// ── Discriminated union ─────────────────────────────────────────────────────
export const StepSchema = z.discriminatedUnion('type', [
    TextStepSchema,
    ConditionStepSchema,
    SetContextStepSchema,
    HandoffStepSchema,
    EncuestaStepSchema,
    HttpStepSchema,
    LlmStepSchema,
    InteractiveStepSchema,
    DocumentStepSchema,
    SendTemplateStepSchema,
    UpdateContactStepSchema,
    FlowTransferStepSchema,
    JsStepSchema,
]);
// ── Flow ────────────────────────────────────────────────────────────────────
export const FlowSchema = z.object({
    id: z.string().regex(/^[a-z][a-z0-9-]{0,63}$/),
    version: z.string().regex(/^\d+\.\d+\.\d+$/, 'SemVer'),
    tenantId: z.string().min(1),
    channel: z.enum(['whatsapp', 'webchat', 'notifications', 'messenger', 'instagram', 'email', 'sms', 'voice']),
    startStepId: StepIdSchema,
    steps: z.array(StepSchema).min(1).max(200),
    /** TTL del estado de conversación en segundos (default 7 días). */
    conversationTtlSeconds: z.number().int().min(60).max(60 * 60 * 24 * 30).default(60 * 60 * 24 * 7),
    /**
     * #142: id de Survey que el encuesta-service iniciará automáticamente al
     * completar este flow (Conversation.FlowCompleted). Si no se define, el
     * handler usa fallback `nps-${tenantId}`. Permite configuración
     * per-flow vs default global.
     */
    autoSurveyId: z.string().min(1).max(120).optional(),
}).superRefine((flow, ctx) => {
    const ids = new Set();
    for (const s of flow.steps) {
        if (ids.has(s.id)) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['steps'], message: `Duplicate step id: ${s.id}` });
        }
        ids.add(s.id);
    }
    if (!ids.has(flow.startStepId)) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['startStepId'], message: `startStepId "${flow.startStepId}" not found in steps` });
    }
    // #170/#171 — Canales cuyo identificador es un teléfono real. Fuera de acá,
    // `from.id` es otra cosa (en webchat, un visitorId anónimo del navegador).
    const CANALES_CON_TELEFONO = ['whatsapp', 'sms', 'notifications'];
    for (const s of flow.steps) {
        if (s.type === 'send-template' && flow.channel !== 'whatsapp') {
            // El orchestrator no despacha webchat a ningún provider, y los demás
            // canales no tienen plantillas de Meta: el envío no saldría nunca y el
            // flujo se quedaría mudo sin que nada falle.
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['steps'],
                message: `send-template step "${s.id}": sólo existe en flows de whatsapp (este es ${flow.channel})`,
            });
        }
        if (s.type === 'update-contact' && !CANALES_CON_TELEFONO.includes(flow.channel)) {
            // Guardar un visitorId como teléfono llenaría la agenda de fichas basura.
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['steps'],
                message: `update-contact step "${s.id}": el canal ${flow.channel} no identifica al contacto por teléfono`,
            });
        }
        // #170 — Meta acepta parámetros posicionales o nombrados, no las dos
        // formas a la vez: mandar ambas hace que rechace el envío entero.
        if (s.type === 'send-template' &&
            s.params?.length &&
            s.namedParams &&
            Object.keys(s.namedParams).length) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['steps'],
                message: `send-template step "${s.id}": usa params o namedParams, no ambos`,
            });
        }
        const refs = [];
        switch (s.type) {
            case 'condition':
                refs.push(...s.branches.map(b => b.goto), s.default);
                if (s.maxAttemptsGoto !== undefined)
                    refs.push(s.maxAttemptsGoto);
                break;
            case 'http':
                refs.push(s.next);
                if (s.onError !== undefined)
                    refs.push(s.onError);
                break;
            case 'llm':
                refs.push(s.next);
                if (s.fallbackGoto !== undefined)
                    refs.push(s.fallbackGoto);
                if (s.buttons)
                    refs.push(...s.buttons.map((b) => b.goto));
                break;
            case 'interactive': {
                refs.push(...s.choices.map((c) => c.goto), s.fallback);
                if (s.presentation === 'buttons' && s.choices.length > 3) {
                    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['steps'], message: `Interactive step "${s.id}": WhatsApp buttons soportan máximo 3 choices` });
                }
                const choiceIds = new Set();
                for (const c of s.choices) {
                    if (choiceIds.has(c.id))
                        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['steps'], message: `Interactive step "${s.id}": duplicate choice.id "${c.id}"` });
                    choiceIds.add(c.id);
                }
                break;
            }
            default:
                refs.push(s.next);
        }
        for (const r of refs) {
            if (r !== null && !ids.has(r)) {
                ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['steps'], message: `Step "${s.id}" references unknown step "${r}"` });
            }
        }
    }
});
export function parseFlow(raw) {
    return FlowSchema.parse(raw);
}
//# sourceMappingURL=index.js.map